import type { Trazo } from "@gestiondoc/shared";
import { getStroke } from "perfect-freehand";

/**
 * De los puntos de un trazo al `d` de un `<path>` SVG. El lápiz es un contorno relleno que
 * adelgaza y engorda con la presión (perfect-freehand, lo mismo que usan tldraw y Excalidraw);
 * el resaltador es una línea de grosor fijo y translúcida. Vive en la plantilla porque lo usan
 * la capa de tinta de la web y el PDF de la hoja llena: el mismo trazo en los dos.
 */

export function pathDeTrazo(t: Trazo, terminado = true): string {
  if (t.herramienta === "resaltador") return lineaDe(t.puntos);
  const entrada: [number, number, number][] = [];
  for (let i = 0; i < t.puntos.length; i += 3) {
    entrada.push([t.puntos[i] ?? 0, t.puntos[i + 1] ?? 0, t.puntos[i + 2] ?? 0.5]);
  }
  const contorno = getStroke(entrada, {
    size: t.grosor * 2,
    thinning: t.presion ? 0.6 : 0.45,
    smoothing: 0.5,
    streamline: 0.4,
    simulatePressure: !t.presion,
    last: terminado,
  });
  return contornoADPath(contorno);
}

function contornoADPath(puntos: number[][]): string {
  const n = puntos.length;
  if (n < 2) return "";
  const f = (v: number) => v.toFixed(1);
  const [x0, y0] = puntos[0] as [number, number];
  let d = `M${f(x0)} ${f(y0)}Q`;
  for (let i = 0; i < n; i++) {
    const [ax, ay] = puntos[i] as [number, number];
    const [bx, by] = puntos[(i + 1) % n] as [number, number];
    d += `${f(ax)} ${f(ay)} ${f((ax + bx) / 2)} ${f((ay + by) / 2)} `;
  }
  return `${d}Z`;
}

function lineaDe(p: number[]): string {
  if (p.length < 3) return "";
  let d = `M${p[0]} ${p[1]}`;
  // Un toque sin arrastre también deja un punto visible.
  if (p.length === 3) return `${d}l0.01 0`;
  for (let i = 3; i < p.length; i += 3) d += `L${p[i]} ${p[i + 1]}`;
  return d;
}

/** ¿Pasa el trazo a menos de `radio` del punto? Para la goma, que borra trazos enteros. */
export function tocaTrazo(t: Trazo, x: number, y: number, radio: number): boolean {
  const r = radio + t.grosor / 2;
  const p = t.puntos;
  const caja = cajaDe(t);
  if (x < caja[0] - r || x > caja[2] + r || y < caja[1] - r || y > caja[3] + r) return false;
  if (p.length === 3) return Math.hypot((p[0] ?? 0) - x, (p[1] ?? 0) - y) <= r;
  for (let i = 3; i < p.length; i += 3) {
    if (distanciaASegmento(x, y, p[i - 3] ?? 0, p[i - 2] ?? 0, p[i] ?? 0, p[i + 1] ?? 0) <= r) {
      return true;
    }
  }
  return false;
}

const cajas = new WeakMap<Trazo, [number, number, number, number]>();
function cajaDe(t: Trazo): [number, number, number, number] {
  const hecha = cajas.get(t);
  if (hecha) return hecha;
  const caja: [number, number, number, number] = [
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  ];
  for (let i = 0; i < t.puntos.length; i += 3) {
    const x = t.puntos[i] ?? 0;
    const y = t.puntos[i + 1] ?? 0;
    if (x < caja[0]) caja[0] = x;
    if (y < caja[1]) caja[1] = y;
    if (x > caja[2]) caja[2] = x;
    if (y > caja[3]) caja[3] = y;
  }
  cajas.set(t, caja);
  return caja;
}

function distanciaASegmento(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const largo = dx * dx + dy * dy;
  const t = largo ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / largo)) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
